import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';

interface ProductAttributeUpdate {
  productId: string;
  cpProductId: string;
  cpItemId: string;
  cpUrl: string;
  cpNameKr: string;
}

interface AttributePayload {
  products: Array<{
    productId: number;
    attributesToSet: Array<{
      key: string;
      value: string;
    }>;
  }>;
}

interface BarcodeInUseBody {
  code?: string;
  conflictingProductId?: number | string;
  conflictingProductName?: string;
}

/** The product a refused barcode already belongs to, as the 409 body names it (SSD-7532). */
export interface BarcodeOwner {
  productId: number;
  /** Null when the server could not read the name. */
  name: string | null;
}

/** A barcode save; a refusal because another product carries the code also names that product. */
export interface BarcodeSaveResult {
  success: boolean;
  message: string;
  owner?: BarcodeOwner;
}

/** The owner a 409 BARCODE_IN_USE body names, or undefined for any other refusal. */
const ownerOfRefusal = (status: number, errorText: string): BarcodeOwner | undefined => {
  if (status !== 409) {
    return undefined;
  }
  try {
    const body = JSON.parse(errorText) as BarcodeInUseBody | null;
    if (body?.code !== 'BARCODE_IN_USE') {
      return undefined;
    }
    const productId = Number(body.conflictingProductId);
    if (!Number.isInteger(productId) || productId <= 0) {
      return undefined;
    }
    return { productId, name: body.conflictingProductName?.trim() || null };
  } catch {
    // A body that is not JSON carries no owner.
    return undefined;
  }
};

/** The server names the product that owns a barcode in a 409 body; the operator needs that name, not the status. */
const describeBarcodeRefusal = (status: number, barcode: string, errorText: string): string => {
  if (status === 409) {
    try {
      const body = JSON.parse(errorText) as BarcodeInUseBody | null;
      if (body?.code === 'BARCODE_IN_USE') {
        const owner = body.conflictingProductName?.trim()
          ? body.conflictingProductName.trim()
          : body.conflictingProductId !== undefined
            ? `product #${body.conflictingProductId}`
            : 'another product';
        return `Barcode ${barcode} already belongs to ${owner}`;
      }
    } catch {
      // A body that is not JSON carries no owner; fall through to the status line.
    }
  }
  return `Failed to save barcode: ${status}`;
};

class ProductAttributesClient {
  /**
   * Update product attributes with Coupang information
   */
  public async updateProductAttributes(data: ProductAttributeUpdate): Promise<{ success: boolean; message: string }> {
    try {
      const apiUrl = this.getApiUrl();

      // Extract parent and child codes from the product/item IDs
      const cpParentCode = data.cpProductId;
      const cpChildCode = data.cpItemId;
      const cpProductCode = `${cpParentCode}-${cpChildCode}`;

      // Build the API payload
      const payload: AttributePayload = {
        products: [
          {
            productId: parseInt(data.productId),
            attributesToSet: [
              {
                key: 'cp_url',
                value: data.cpUrl,
              },
              {
                key: 'cp_product_code',
                value: cpProductCode,
              },
              {
                key: 'cp_parent_code',
                value: cpParentCode,
              },
              {
                key: 'cp_child_code',
                value: cpChildCode,
              },
              {
                key: 'cp_name_kr',
                value: data.cpNameKr,
              },
            ],
          },
        ],
      };

      console.info('📡 Updating product attributes:', {
        productId: data.productId,
        payload,
      });

      // Make API call to update product attributes
      const response = await fetch(`${apiUrl}/product/attributes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getConfig().apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.info('✅ Successfully updated product attributes:', result);

        return {
          success: true,
          message: `Product attributes updated successfully! CP URL: ${data.cpUrl}`,
        };
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update product attributes:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });

        return {
          success: false,
          message: `Failed to update product: ${response.status} ${response.statusText}`,
        };
      }
    } catch (error) {
      console.error('❌ Error updating product attributes:', error);
      return {
        success: false,
        message: `Error updating product: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  public async updateBarcode(productId: number, barcode: string): Promise<BarcodeSaveResult> {
    try {
      const apiUrl = this.getApiUrl();

      const payload: AttributePayload = {
        products: [
          {
            productId,
            attributesToSet: [{ key: 'barcode', value: barcode }],
          },
        ],
      };

      const response = await fetch(`${apiUrl}/product/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': getConfig().apiKey },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const productResult = result.results?.[0];
        if (productResult?.success === false) {
          const errorMsg = productResult.error || 'Unknown error';
          console.error('Barcode validation failed:', errorMsg);
          return { success: false, message: errorMsg };
        }
        return { success: true, message: `Barcode saved: ${barcode}` };
      } else {
        const errorText = await response.text();
        console.error('Failed to update barcode:', { status: response.status, error: errorText });
        const owner = ownerOfRefusal(response.status, errorText);
        return {
          success: false,
          message: describeBarcodeRefusal(response.status, barcode, errorText),
          ...(owner ? { owner } : {}),
        };
      }
    } catch (error) {
      console.error('Error updating barcode:', error);
      return { success: false, message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }

  async copyUrlToClipboard(url: string): Promise<{ success: boolean; message: string }> {
    try {
      await navigator.clipboard.writeText(url);
      return {
        success: true,
        message: `URL copied to clipboard: ${url}`,
      };
    } catch (error) {
      console.error('❌ Failed to copy URL to clipboard:', error);
      return {
        success: false,
        message: 'Failed to copy URL to clipboard',
      };
    }
  }

  private getApiUrl(): string {
    return getAdminApiUrl(AdminApiDomain.PRODUCT);
  }
}

export const productAttributesClient = new ProductAttributesClient();
export type { ProductAttributeUpdate };
